/**
 * SRC-20 Feature — Public API.
 *
 * Provides shielded SRC-20 token balance display + transfer on Seismic chains.
 * See `specs/005-seismic-src20-balances/spec.md`.
 */
import { createFeatureHandle } from '@/features/__core__'
import type { Src20Contract } from './contract'

// Feature flag mapped to FEATURES.SHIELDED_EVM in createFeatureHandle's FEATURE_FLAG_MAPPING.
export const Src20Feature = createFeatureHandle<Src20Contract>('src20')

export type { Src20Contract } from './contract'
export type {
  Src20TokenInfo,
  Src20BalanceState,
  Src20ReaderError,
  BalanceValueCacheEntry,
  Src20ModuleStatus,
  Src20SendParams,
} from './types'

// Hooks — direct exports (always loaded, not lazy).
export { useSrc20Config } from './hooks/useSrc20Config'
export { useSrc20TokenList } from './hooks/useSrc20TokenList'
export { useSrc20Balance } from './hooks/useSrc20Balance'
export { useIsSrc20ModuleEnabled, invalidateModuleEnabledCache } from './hooks/useIsSrc20ModuleEnabled'
export { useBalanceValueCache } from './hooks/useBalanceValueCache'
export { useIsSafeOwnerOnSeismic } from './hooks/useIsSafeOwnerOnSeismic'
export { useRevealedSrc20Amount } from './hooks/useRevealedSrc20Amount'

// Registry helpers — useful for the queue label decoder and analytics.
export { getSrc20Config, hasSrc20Registry, buildTokenPageUrl, buildTokenListUrl, SRC20_REGISTRY } from './registry'
export type { Src20ChainConfig } from './registry'

// Selectors / ABI — exposed for the queue label decoder and tests.
export { SRC20_TRANSFER_SELECTOR, SRC20_BALANCE_SELECTOR, READER_ABI } from './constants'

// Calldata helpers.
export { buildSrc20TransferData, decodeSrc20TransferData } from './services/buildShieldedTransfer'

// Queued-transfer amount reveal — pure resolver (unit-tested) + its types.
export { resolveRevealedSrc20Amount } from './services/revealSrc20Amount'
export type { RevealedSrc20Amount, ResolveRevealedSrc20AmountArgs } from './services/revealSrc20Amount'
